import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import Loader from '@/common/presentation/components/Loader';
import { RwAlert } from '@/common/presentation/components/rw';

export interface LibraryGalleryError {
  message: string;
  onRetry?: () => void;
}

export interface LibraryGalleryStatusProps {
  isLoading: boolean;
  error: LibraryGalleryError | null;
  isEmpty: boolean;
  emptyState: ReactNode;
  loadingLabel: string;
  testIdPrefix: string;
}

/**
 * The gallery's non-grid states: loading (dev's shared `Loader`, `role="status"` polite),
 * error (`RwAlert` with a retry action when one is given), empty (the caller's `EmptyState`).
 * Exactly one of these renders at a time — the grid itself only mounts once none apply.
 */
export function LibraryGalleryStatus({ isLoading, error, isEmpty, emptyState, loadingLabel, testIdPrefix }: LibraryGalleryStatusProps) {
  if (error) {
    return (
      <Box data-testid={`${testIdPrefix}:error`}>
        <RwAlert
          tone="critical"
          title={error.message}
          actionLabel={error.onRetry ? 'Retry' : undefined}
          onAction={error.onRetry}
          actionTestId={`${testIdPrefix}:retry`}
        />
      </Box>
    );
  }

  if (isLoading) {
    return <Loader label={loadingLabel} />;
  }

  if (isEmpty) {
    return <Box data-testid={`${testIdPrefix}:empty`}>{emptyState}</Box>;
  }

  return null;
}
